import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Icon, message } from '@/components';
import { DEVICE_TYPE, DEVICE_STATUS_REQUEST } from '@/api';
import { useIntl } from 'umi';
import { useLocalStore, useObserver } from 'mobx-react';
import { user as userModel, rtc } from '@/models';

enum OPERATION_TYPE {
  AUDIO = 1,
  VIDEO = 2,
  PERMISSIONS = 3,
}
enum OPERATION_STATE {
  OPEN = 1,
  CLOSE = 0,
}

export interface HostOperationResp {
  channelId: number;
  uid: string;
  type: OPERATION_TYPE;
  state: OPERATION_STATE;
}

export default function useHostOperation() {
  const { rtc: RTCInstance, user } = useLocalStore(() => {
    return {
      user: userModel,
      rtc,
    };
  });
  const [isShowModal, setIsShowModal] = useState(false);
  const { formatMessage } = useIntl();

  const hostOperation = useCallback(
    async (resp: HostOperationResp) => {
      // 暂时不用处理
      if (resp.type === OPERATION_TYPE.PERMISSIONS || resp.uid !== user.uid) return;
      let title = formatMessage({ id: 'HOST_OPEN_MIC' });
      if (resp.type === OPERATION_TYPE.VIDEO) {
        title = formatMessage({ id: 'HOST_OPEN_CAMERA' });
      }
      // 如果是开启设备，则询问用户
      if (resp.state === OPERATION_STATE.OPEN) {
        if (isShowModal) return;
        setIsShowModal(true);
        Modal.confirm({
          title: (
            <>
              <Icon
                name="icon-warning"
                size={24}
                style={{
                  color: ' #FF9A2D',
                  verticalAlign: 'middle',
                  display: 'inline-block',
                  marginRight: '12px',
                }}
              />
              {title}
            </>
          ),
          closable: !window.isMobile,
          okText: formatMessage({ id: 'CONFIRM' }),
          cancelText: formatMessage({ id: 'CANCEL' }),
          okButtonProps: {
            danger: true,
          },
          onCancel: () => {
            setIsShowModal(false);
          },
          onOk: async () => {
            if (resp.type === OPERATION_TYPE.AUDIO) {
              if (RTCInstance.micIsNotAvailable) {
                message.info({
                  content: formatMessage({ id: 'NO_DEVICE' }),
                  noIcon: true,
                });
                return;
              }
              try {
                await user?.updateDeviceStatus(
                  String(RTCInstance.cid),
                  DEVICE_TYPE.AUDIO,
                  DEVICE_STATUS_REQUEST.OPEN
                );
                await RTCInstance.openMic(true);
              } catch (err) {
                console.log(err);
              }
            }
            if (resp.type === OPERATION_TYPE.VIDEO) {
              if (RTCInstance.cameraIsNotAvailable) {
                message.info({
                  content: formatMessage({ id: 'NO_DEVICE' }),
                  noIcon: true,
                });
                return;
              }
              try {
                await RTCInstance.openCamera(true);
                await user?.updateDeviceStatus(
                  String(RTCInstance.cid),
                  DEVICE_TYPE.VIDEO,
                  DEVICE_STATUS_REQUEST.OPEN
                );
              } catch (e) {
                console.error(e);
              }
            }
            setIsShowModal(false);
          },
        });
      } else {
        try {
          if (resp.type === OPERATION_TYPE.AUDIO) {
            await user?.updateDeviceStatus(
              String(RTCInstance.cid),
              DEVICE_TYPE.AUDIO,
              DEVICE_STATUS_REQUEST.CLOSE
            );
            // await user?.quiet(String(meeting?.cid), DEVICE_STATUS_REQUEST.CLOSE);
            await RTCInstance.closeMic();
            message.info({
              content: formatMessage({ id: 'TURNED_OFF_MIC_BY_HOST' }),
              noIcon: true,
            });
          }
          if (resp.type === OPERATION_TYPE.VIDEO) {
            await RTCInstance.closeCamera();
            await user?.updateDeviceStatus(
              String(RTCInstance.cid),
              DEVICE_TYPE.VIDEO,
              DEVICE_STATUS_REQUEST.CLOSE
            );
            message.info({
              content: formatMessage({ id: 'TURNED_OFF_CAMERA_BY_HOST' }),
              noIcon: true,
            });
          }
        } catch (e) {
          console.error(e);
        }
      }
    },
    [isShowModal]
  );

  return hostOperation;
}
